import React from 'react'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import {
  Alert,
  BodyText,
  Button,
  CaptionText,
  HeadingText,
  PictureIcon,
} from '@paypalcorp/pp-react'
import styles from './crowdfunding_cover_image.less'
import CrossFadeTransition from 'components/common/transitions/cross_fade'
import ImageUploadField from 'components/common/image_upload_field'
import { trackClick } from 'lib/fpti_analytics'
import { getI18n } from 'client/i18n'
import CrowdFundingImageAdjust from '../crowdfunding_image_adjust'
import CrowdFundingImagePreview from 'components/crowdfunding_image_preview'
import {
  COVER_IMAGE_MAX_TOTAL_SIZE,
  COVER_IMAGE_UPLOAD_ERRORS,
} from 'shared/shared_consts'

const {
  FILE_INVALID_TYPE,
  FILE_TOO_LARGE,
  FILE_TOO_SMALL,
  TOO_MANY_FILES,
} = COVER_IMAGE_UPLOAD_ERRORS
const DEFAULT_PAN = 50
const i18n = getI18n('components/pages/crowdfunding/crowdfunding_cover_image')

class CrowdFundingCoverImage extends React.Component {
  static propTypes = {
    fields: PropTypes.object.isRequired,
    showDoneButton: PropTypes.bool,
    showTitle: PropTypes.bool,
    showPreviewComponent: PropTypes.bool,
    categoryImage: PropTypes.string,
  }

  state = {
    isLoading: false,
    uploadError: null,
  }

  constructor(props) {
    super(props)
    this.child = React.createRef()
  }

  handleImageRemove = e => {
    e.preventDefault()
    const {
      fields: { backgroundImage, backgroundImageFilename, backgroundPan },
    } = this.props
    this.setState({ uploadError: null })
    trackClick({
      clickName: 'dw_giving_edit_fundraiser_remove_photo_pressed',
      data: {
        event_name: 'dw_giving_edit_fundraiser_remove_photo_pressed',
      },
    })

    backgroundPan.onChange(DEFAULT_PAN)
    backgroundImage.onChange(undefined)
    backgroundImageFilename.onChange(undefined)
  }

  handleImageChange = val => {
    const {
      fields: { backgroundImage, backgroundImageFilename, backgroundPan },
    } = this.props
    this.setState({ uploadError: null })
    trackClick({
      clickName: 'dw_giving_edit_fundraiser_change_photo_pressed',
      data: {
        event_name: 'dw_giving_edit_fundraiser_change_photo_pressed',
      },
    })

    backgroundPan.onChange(DEFAULT_PAN)
    backgroundImage.onChange(val.url)
    backgroundImageFilename.onChange(val.name || backgroundImageFilename.value)
  }

  handleImageLoad = isLoading => {
    this.setState({ isLoading })
  }

  imageUploadClick = () => {
    this.child.current.openFileDialog()
  }

  handleImageUploadError = imageLoadError => {
    this.setState({ uploadError: imageLoadError })
  }

  mapErrorToContent = (errorCode = FILE_INVALID_TYPE) => {
    const errorCodeMapping = {}

    errorCodeMapping[FILE_INVALID_TYPE] = 'file_invalid_type'
    errorCodeMapping[FILE_TOO_LARGE] = 'file_too_large'
    errorCodeMapping[FILE_TOO_SMALL] = 'file_too_small'
    errorCodeMapping[TOO_MANY_FILES] = 'too_many_files'
    return errorCodeMapping[errorCode]
  }

  getErrorMessage = errorCode => {
    const key = this.mapErrorToContent(this.state.uploadError.code)
    return i18n(key)
  }

  render() {
    const {
      fields: { backgroundImage = {}, backgroundPan },
      showDoneButton,
      showTitle,
      showPreviewComponent,
      categoryImage,
    } = this.props

    return (
      <div>
        {showTitle && (
          <HeadingText size="md" styleName="title_cf">
            {i18n('title_cf')}
          </HeadingText>
        )}
        <CrossFadeTransition>
          {backgroundImage.value ? (
            <div key="with-value">
              <div
                styleName="image-container"
                className={this.state.isLoading ? 'vx_has-spinner-medium' : ''}
              >
                {/* To manage older version from cf edit */}
                {showPreviewComponent ? (
                  <CrowdFundingImagePreview
                    imageUrl={backgroundImage.value}
                    pan={parseInt(backgroundPan.value)}
                    onPanChange={backgroundPan.onChange}
                    onImageChange={this.handleImageChange}
                    onImageLoad={this.handleImageLoad}
                    onImageRemove={this.handleImageRemove}
                    categoryImage={categoryImage}
                  />
                ) : (
                  <CrowdFundingImageAdjust
                    onImageRemove={this.handleImageRemove}
                    imageUrl={backgroundImage.value}
                    onImageChange={this.handleImageChange}
                  />
                )}
              </div>
            </div>
          ) : (
            <div key="without-value">
              <div
                className={this.state.isLoading ? 'vx_has-spinner-medium' : ''}
              >
                <ImageUploadField
                  onImageLoad={this.handleImageLoad}
                  onError={this.handleImageUploadError}
                  restrictedTotalSize={COVER_IMAGE_MAX_TOTAL_SIZE}
                  onChange={this.handleImageChange}
                  ref={this.child}
                >
                  <div styleName="upload-info">
                    <PictureIcon styleName="upload-info-icon" />
                    <BodyText strong styleName="upload-info-text">
                      {i18n('upload')}
                    </BodyText>
                  </div>
                </ImageUploadField>
                <CaptionText styleName="tip">{i18n('upload_tip')}</CaptionText>
                {this.state.uploadError ? (
                  <Alert type="error">
                    {this.getErrorMessage(this.state.uploadError.code)}
                  </Alert>
                ) : null}
              </div>
            </div>
          )}
          {showDoneButton && (
            <Button
              data-pa-click="done"
              data-nemo="done"
              styleName="done-btn"
              className="vx_btn vx_btn-block"
            >
              {i18n('done')}
            </Button>
          )}
        </CrossFadeTransition>
      </div>
    )
  }
}
export default cssModules(CrowdFundingCoverImage, styles)
